import { supabase } from './supabase';
import type { Database } from './database.types';
import { getCreativeFamilyName } from './creatives';

type ProjectMessage = Database['public']['Tables']['project_messages']['Row'];
type ProjectMessageInsert = Database['public']['Tables']['project_messages']['Insert'];
type ProjectMessageUpdate = Database['public']['Tables']['project_messages']['Update'];

export interface MessageWithUser {
  id: string;
  projectId: string;
  userId: string;
  messageText: string;
  messageType: string;
  replyToMessageId?: string;
  editedAt?: string;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  replyToMessage?: {
    id: string;
    messageText: string;
    user: {
      name: string;
      email: string;
    };
    mentions?: {
      users: Array<{
        id: string;
        name: string;
        email: string;
      }>;
      creatives: Array<{
        id: string;
        name: string;
        type: string;
      }>;
    };
  };
  mentions?: {
    users: Array<{
      id: string;
      name: string;
      email: string;
    }>;
    creatives: Array<{
      id: string;
      name: string;
      type: string;
    }>;
  };
}

export interface SendMessageRequest {
  projectId: string;
  messageText: string;
  messageType?: 'text' | 'file' | 'system';
  replyToMessageId?: string;
}

export class MessageService {
  /**
   * Send a new message
   */
  static async sendMessage(request: SendMessageRequest): Promise<MessageWithUser> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user can access this project
      const canAccess = await this.canAccessProjectMessages(request.projectId);
      if (!canAccess) {
        throw new Error('You do not have permission to send messages in this project');
      }

      const messageData: ProjectMessageInsert = {
        project_id: request.projectId,
        user_id: user.id,
        message_text: request.messageText,
        message_type: request.messageType || 'text',
        reply_to_message_id: request.replyToMessageId || null
      };

      const { data: message, error } = await supabase
        .from('project_messages')
        .insert(messageData)
        .select(`
          *,
          users!project_messages_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        throw new Error(`Failed to send message: ${error.message}`);
      }

      // Parse mentions from the message
      const { userMentions, creativeMentions } = this.parseMentions(request.messageText);
      
      let mentions;
      
      if (userMentions.length > 0 || creativeMentions.length > 0) {
        const [users, creatives] = await Promise.all([
          userMentions.length > 0 ? this.getProjectMembers(request.projectId) : Promise.resolve([]),
          creativeMentions.length > 0 ? this.getProjectCreatives(request.projectId) : Promise.resolve([])
        ]);
        
        mentions = {
          users: users.filter(user => userMentions.includes(user.id)),
          creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
        };
      }

      return {
        id: message.id,
        projectId: message.project_id,
        userId: message.user_id,
        messageText: message.message_text,
        messageType: message.message_type || 'text',
        replyToMessageId: message.reply_to_message_id || undefined,
        editedAt: message.edited_at || undefined,
        deletedAt: message.deleted_at || undefined,
        createdAt: message.created_at || '',
        updatedAt: message.updated_at || '',
        user: (message.users as any) || { id: '', name: '', email: '' },
        mentions
      };
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  }

  /**
   * Get messages for a project with pagination
   */
  static async getProjectMessages(
    projectId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<MessageWithUser[]> {
    try {
      // Check if user can access this project
      const canAccess = await this.canAccessProjectMessages(projectId);
      if (!canAccess) {
        throw new Error('You do not have permission to access messages in this project');
      }

      const { data: messages, error } = await supabase
        .from('project_messages')
        .select(`
          *,
          users!project_messages_user_id_fkey(id, name, email, avatar)
        `)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw new Error(`Failed to get messages: ${error.message}`);
      }

      // Get all reply message IDs and fetch them separately
      const replyMessageIds = (messages || [])
        .map(msg => msg.reply_to_message_id)
        .filter((id): id is string => Boolean(id));

      let replyMessages: any[] = [];
      if (replyMessageIds.length > 0) {
        const { data: replyData } = await supabase
          .from('project_messages')
          .select(`
            id,
            message_text,
            users!project_messages_user_id_fkey(name, email)
          `)
          .in('id', replyMessageIds);
        
        replyMessages = replyData || [];
      }

      // Process messages and add mentions
      const processedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          const { userMentions, creativeMentions } = this.parseMentions(message.message_text);
          let mentions;
          
          if (userMentions.length > 0 || creativeMentions.length > 0) {
            const [users, creatives] = await Promise.all([
              userMentions.length > 0 ? this.getProjectMembers(projectId) : Promise.resolve([]),
              creativeMentions.length > 0 ? this.getProjectCreatives(projectId) : Promise.resolve([])
            ]);
            
            mentions = {
              users: users.filter(user => userMentions.includes(user.id)),
              creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
            };
          }

          // Find reply message if exists
          const replyMessage = message.reply_to_message_id ? 
            replyMessages.find(r => r.id === message.reply_to_message_id) : null;

          return {
            id: message.id,
            projectId: message.project_id,
            userId: message.user_id,
            messageText: message.message_text,
            messageType: message.message_type || 'text',
            replyToMessageId: message.reply_to_message_id || undefined,
            editedAt: message.edited_at || undefined,
            deletedAt: message.deleted_at || undefined,
            createdAt: message.created_at || '',
            updatedAt: message.updated_at || '',
            user: (message.users as any) || { id: '', name: '', email: '' },
            replyToMessage: replyMessage ? {
              id: replyMessage.id,
              messageText: replyMessage.message_text,
              user: {
                name: replyMessage.users?.name || '',
                email: replyMessage.users?.email || ''
              }
            } : undefined,
            mentions
          };
        })
      );

      return processedMessages;
    } catch (error) {
      console.error('Get project messages error:', error);
      throw error;
    }
  }

  /**
   * Get recent messages for a project
   */
  static async getRecentMessages(
    projectId: string,
    limit: number = 50
  ): Promise<MessageWithUser[]> {
    try {
      // Check if user can access this project
      const canAccess = await this.canAccessProjectMessages(projectId);
      if (!canAccess) {
        throw new Error('You do not have permission to access messages in this project');
      }

      const { data: messages, error } = await supabase
        .from('project_messages')
        .select(`
          *,
          users!project_messages_user_id_fkey(id, name, email, avatar)
        `)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to get messages: ${error.message}`);
      }

      // Get all reply message IDs and fetch them separately
      const replyMessageIds = (messages || [])
        .map(msg => msg.reply_to_message_id)
        .filter((id): id is string => Boolean(id));

      let replyMessages: any[] = [];
      if (replyMessageIds.length > 0) {
        const { data: replyData } = await supabase
          .from('project_messages')
          .select(`
            id,
            message_text,
            users!project_messages_user_id_fkey(name, email)
          `)
          .in('id', replyMessageIds);
        
        replyMessages = replyData || [];
      }

      // Process messages and add mentions
      const processedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          const { userMentions, creativeMentions } = this.parseMentions(message.message_text);
          let mentions;
          
          if (userMentions.length > 0 || creativeMentions.length > 0) {
            const [users, creatives] = await Promise.all([
              userMentions.length > 0 ? this.getProjectMembers(projectId) : Promise.resolve([]),
              creativeMentions.length > 0 ? this.getProjectCreatives(projectId) : Promise.resolve([])
            ]);
            
            mentions = {
              users: users.filter(user => userMentions.includes(user.id)),
              creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
            };
          }

          // Find reply message if exists
          const replyMessage = message.reply_to_message_id ? 
            replyMessages.find(r => r.id === message.reply_to_message_id) : null;

          return {
            id: message.id,
            projectId: message.project_id,
            userId: message.user_id,
            messageText: message.message_text,
            messageType: message.message_type || 'text',
            replyToMessageId: message.reply_to_message_id || undefined,
            editedAt: message.edited_at || undefined,
            deletedAt: message.deleted_at || undefined,
            createdAt: message.created_at || '',
            updatedAt: message.updated_at || '',
            user: (message.users as any) || { id: '', name: '', email: '' },
            replyToMessage: replyMessage ? {
              id: replyMessage.id,
              messageText: replyMessage.message_text,
              user: {
                name: replyMessage.users?.name || '',
                email: replyMessage.users?.email || ''
              }
            } : undefined,
            mentions
          };
        })
      );

      return processedMessages;
    } catch (error) {
      console.error('Get recent messages error:', error);
      throw error;
    }
  }

  /**
   * Edit an existing message
   */
  static async editMessage(
    messageId: string,
    newMessageText: string
  ): Promise<MessageWithUser> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user owns this message
      const { data: existingMessage, error: checkError } = await supabase
        .from('project_messages')
        .select('user_id, project_id, message_text')
        .eq('id', messageId)
        .single();

      if (checkError) {
        throw new Error('Message not found');
      }

      if (existingMessage.user_id !== user.id) {
        throw new Error('You can only edit your own messages');
      }

      const { data: message, error } = await supabase
        .from('project_messages')
        .update({
          message_text: newMessageText,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .select(`
          *,
          users!project_messages_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        throw new Error(`Failed to edit message: ${error.message}`);
      }

      // Parse mentions from the updated message
      const { userMentions, creativeMentions } = this.parseMentions(newMessageText);
      let mentions;
      
      if (userMentions.length > 0 || creativeMentions.length > 0) {
        const [users, creatives] = await Promise.all([
          userMentions.length > 0 ? this.getProjectMembers(existingMessage.project_id) : Promise.resolve([]),
          creativeMentions.length > 0 ? this.getProjectCreatives(existingMessage.project_id) : Promise.resolve([])
        ]);
        
        mentions = {
          users: users.filter(user => userMentions.includes(user.id)),
          creatives: creatives.filter(creative => creativeMentions.includes(creative.id))
        };
      }

      return {
        id: message.id,
        projectId: message.project_id,
        userId: message.user_id,
        messageText: message.message_text,
        messageType: message.message_type || 'text',
        replyToMessageId: message.reply_to_message_id || undefined,
        editedAt: message.edited_at || undefined,
        deletedAt: message.deleted_at || undefined,
        createdAt: message.created_at || '',
        updatedAt: message.updated_at || '',
        user: (message.users as any) || { id: '', name: '', email: '' },
        mentions
      };
    } catch (error) {
      console.error('Edit message error:', error);
      throw error;
    }
  }

  /**
   * Delete a message (soft delete)
   */
  static async deleteMessage(messageId: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const updateData: ProjectMessageUpdate = {
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('project_messages')
        .update(updateData)
        .eq('id', messageId)
        .eq('user_id', user.id); // Only allow deleting own messages

      if (error) {
        throw new Error(`Failed to delete message: ${error.message}`);
      }
    } catch (error) {
      console.error('Delete message error:', error);
      throw error;
    }
  }

  /**
   * Check if user can access project messages
   * Enhanced with organization-level team role permissions
   */
  static async canAccessProjectMessages(projectId: string): Promise<boolean> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return false;
      }

      // Check if user is project owner
      const { data: project } = await supabase
        .from('projects')
        .select(`
          user_id,
          users!projects_user_id_fkey(organization_id)
        `)
        .eq('id', projectId)
        .single();

      if (!project) {
        return false;
      }

      if (project.user_id === user.id) {
        return true;
      }

      // Check organization-level team role permissions
      const { data: userProfile } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', user.id)
        .single();

      const projectOwnerOrgId = (project.users as any)?.organization_id;
      if (userProfile && userProfile.organization_id === projectOwnerOrgId) {
        // Organization admins can access all organization projects
        if (userProfile.team_role === 'admin') {
          return true;
        }
        
        // Check if user has project access permission based on team role
        if (userProfile.team_role === 'editor' || userProfile.team_role === 'viewer') {
          return true; // All team members in same organization can access projects
        }
      }

      // Check if user is project-level team member
      const { data: membership } = await supabase
        .from('team_members')
        .select('id')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .eq('invitation_status', 'accepted')
        .single();

      return !!membership;
    } catch (error) {
      console.error('Access check error:', error);
      return false;
    }
  }

  /**
   * Get message count for a project
   */
  static async getMessageCount(projectId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('project_messages')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .is('deleted_at', null);

      if (error) {
        throw new Error(`Failed to get message count: ${error.message}`);
      }

      return count || 0;
    } catch (error) {
      console.error('Get message count error:', error);
      return 0;
    }
  }

  /**
   * Format message timestamp for display
   */
  static formatMessageTime(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      // Less than 24 hours - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      // Less than a week - show day and time
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      // More than a week - show date and time
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  }

  /**
   * Get user initials for avatar fallback
   */
  static getUserInitials(name: string, email: string): string {
    if (name && name.trim()) {
      const names = name.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    
    if (email) {
      return email[0].toUpperCase();
    }
    
    return 'U';
  }

  /**
   * Check if message belongs to current user
   */
  static async isOwnMessage(messageId: string): Promise<boolean> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return false;
      }

      const { data: message } = await supabase
        .from('project_messages')
        .select('user_id')
        .eq('id', messageId)
        .single();

      return message?.user_id === user.id;
    } catch (error) {
      console.error('Check message ownership error:', error);
      return false;
    }
  }

  /**
   * Parse mentions from message text
   */
  static parseMentions(messageText: string): {
    userMentions: string[];
    creativeMentions: string[];
  } {
    // Updated regex patterns to match UUIDs with hyphens
    const userMentions = [...messageText.matchAll(/@user:([\w-]+)/g)].map(match => match[1]);
    const creativeMentions = [...messageText.matchAll(/@creative:([\w-]+)/g)].map(match => match[1]);
    
    return {
      userMentions,
      creativeMentions
    };
  }

  /**
   * Format mentions in message text for display
   */
  static formatMentionsForDisplay(messageText: string, mentions?: MessageWithUser['mentions']): string {
    let formattedText = messageText;
    
    // Replace user mentions (updated regex to match UUIDs with hyphens)
    if (mentions?.users) {
      mentions.users.forEach(user => {
        const pattern = new RegExp(`@user:${user.id}`, 'g');
        formattedText = formattedText.replace(pattern, `@${user.name || user.email}`);
      });
    }
    
    // Replace creative mentions (updated regex to match UUIDs with hyphens)
    if (mentions?.creatives) {
      mentions.creatives.forEach(creative => {
        const pattern = new RegExp(`@creative:${creative.id}`, 'g');
        formattedText = formattedText.replace(pattern, `@${creative.name}`);
      });
    }
    
    return formattedText;
  }

  /**
   * Get project team members for mentions
   */
  static async getProjectMembers(projectId: string): Promise<Array<{
    id: string;
    name: string;
    email: string;
  }>> {
    try {
      const { data: members, error } = await supabase
        .from('team_members')
        .select(`
          user_id,
          users!team_members_user_id_fkey(id, name, email)
        `)
        .eq('project_id', projectId)
        .eq('invitation_status', 'accepted');

      if (error) throw error;

      // Also get project owner
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          user_id,
          users!projects_user_id_fkey(id, name, email)
        `)
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      const allMembers = [];
      
      // Add project owner
      if (project?.users) {
        allMembers.push({
          id: (project.users as any).id,
          name: (project.users as any).name || (project.users as any).email,
          email: (project.users as any).email
        });
      }
      
      // Add team members
      if (members) {
        members.forEach(member => {
          if (member.users) {
            allMembers.push({
              id: (member.users as any).id,
              name: (member.users as any).name || (member.users as any).email,
              email: (member.users as any).email
            });
          }
        });
      }

      // Remove duplicates
      const unique = allMembers.filter((member, index, self) => 
        index === self.findIndex(m => m.id === member.id)
      );

      return unique;
    } catch (error) {
      console.error('Failed to get project members:', error);
      return [];
    }
  }

  /**
   * Get project creatives for mentions
   */
  static async getProjectCreatives(projectId: string): Promise<Array<{
    id: string;
    name: string;
    type: string;
  }>> {
    try {
      const { data: creatives, error } = await supabase
        .from('creatives')
        .select('id, name, family_name, type')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return creatives?.map(creative => ({
        id: creative.id,
        name: getCreativeFamilyName(creative as any) || `Untitled ${creative.type}`,
        type: creative.type
      })) || [];
    } catch (error) {
      console.error('Failed to get project creatives:', error);
      return [];
    }
  }
}

export default MessageService; 